import React, { useEffect, useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";
import CustomText from "../components/common/general/CustomText/CustomText";
import { MotiView } from "moti";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import {
  GetAssessmentSubmissionsByFacultyDocument,
  GetAssessmentSubmissionsDetailsDocument,
  GetAssessmentSubmissionAttemptHistoryDocument,
  GetGradingCenterDashboardDataDocument,
  GetUserSubmissionsByFacultyDocument,
  useGetAssessmentSubmissionsByFacultyQuery,
  useGetAssessmentSubmissionsDetailsQuery,
  useGetAssessmentSubmissionAttemptHistoryQuery,
  useGetCoursesByFacultyQuery,
  useGetGradingCenterDashboardDataQuery,
  useGetUserSubmissionsByFacultyQuery,
  useMeQuery,
  useUpdateAssessmentSubmissionGradeMutation,
  useUpdateStudentAssignmentGradeMutation,
  type UpdateAssessmentSubmissionUpdatedGrade,
} from "../graphql/generated/graphql";
import DashboardDataContainer from "../containers/customCoursework/TeacherGradingUIComponents/DashboardDataContainer";
import GradingTableContainer from "../containers/customCoursework/TeacherGradingUIComponents/GradingsTableContainer";
import CustomMultiSelect from "../components/common/general/CustomMultiSelect/CustomMultiSelect";
import AssessmentGradingModalContainer from "../containers/customCoursework/TeacherGradingUIComponents/AssessmentGradingModalContainer";
import AssignmentSubmissionGradingModal from "../containers/customCoursework/TeacherGradingUIComponents/AssignmentSubmissionGradingModalContainer";
import usePagination from "../hooks/usePagination";
import CustomSearchInput from "../components/common/general/CustomSearchInput/CustomSearchInput";
import SortingDropdown from "../components/common/TableControls/SortingDropdown/SortingDropdown";
import useSortingDropdown, { SORT_ITEMS } from "../hooks/useSortingDropdown";
import DefaultLayout from "../layouts/DefaultLayout.web";
import { useAppConfig } from "../AppConfigProvider";

enum ModalState {
  CLOSED = "CLOSED",
  VIEW_ASSESSMENT_RESPONSE = "VIEW_ASSESSMENT_RESPONSE",
  VIEW_ASSIGNMENT_RESPONSE = "VIEW_ASSIGNMENT_RESPONSE",
  EDIT_SCORE = "EDIT_SCORE",
}

export interface GradingData {
  userId?: string;
  userName?: string;
  status?: "RUNNING" | "COMPLETED";
  completedAt?: string;
  startedAt?: string;
  totalPoints?: number;
  maximumPoints?: number;
  hasTeacherGraded?: boolean;
  hasTeacherAdjustedGrade?: boolean;
  timeLimit?: number;
  assessmentId?: string;
  created?: string;
  lastModified?: string;
  moduleName?: string;
  courseName?: string;
  type?: string;
  id?: string;
  questions?: {
    id: string;
    question: string;
    totalScore: number;
    rubrics: {
      id: string;
      component: string;
      points: number;
      pointsAwarded: number;
      maxPoints: number;
      remarks: string;
    }[];
    userAnswer: {
      answer: string;
      createdAt: string;
      totalPoints: number;
      maximumPoints: number;
    };
  }[];
}

const ITEMS_PER_PAGE = 3;

const GradingCenterView = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [modalState, setModalState] = React.useState<ModalState>(ModalState.CLOSED);
  const [selectedGradingData, setSelectedGradingData] = React.useState<GradingData | null>(null);
  const [selectedCourses, setSelectedCourses] = React.useState<string[]>([]);
  const [selectedModules, setSelectedModules] = React.useState<string[]>([]);
  const [updateError, setUpdateError] = React.useState<string | null>(null);
  const [actionResult, setActionResult] = React.useState<string | null>(null);

  const appConfig = useAppConfig();
  const schoolId = appConfig.axioSchools[0];

  const { sortBy, setSortBy, sortDropdownOpen, setSortDropdownOpen } = useSortingDropdown({
    defaultSortBy: "date_desc",
  });

  const { data: meData } = useMeQuery();

  const {
    page: assessmentsPage,
    setPage: setAssessmentsPage,
    pageSize: assessmentsPageSize,
    setPageSize: setAssessmentsPageSize,
  } = usePagination({
    itemsPerPage: ITEMS_PER_PAGE,
  });

  const {
    page: submissionsPage,
    setPage: setSubmissionsPage,
    pageSize: submissionsPageSize,
    setPageSize: setSubmissionsPageSize,
  } = usePagination({
    itemsPerPage: ITEMS_PER_PAGE,
  });

  const [updateAssessmentSubmissionGrade] = useUpdateAssessmentSubmissionGradeMutation({
    refetchQueries: [
      {
        query: GetAssessmentSubmissionsByFacultyDocument,
        variables: {
          input: {
            facultyId: meData?.me?.id || "",
            searchQuery,
            page: assessmentsPage,
            pageSize: assessmentsPageSize,
            courseIds: selectedCourses,
            moduleIds: selectedModules,
            sortBy,
          },
        },
      },
      {
        query: GetGradingCenterDashboardDataDocument,
        variables: {
          facultyId: meData?.me?.id || "",
        },
      },
      {
        query: GetAssessmentSubmissionsDetailsDocument,
        variables: {
          assessmentSubmissionId: selectedGradingData?.id || "",
        },
      },
      {
        query: GetAssessmentSubmissionAttemptHistoryDocument,
        variables: {
          assessmentSubmissionId: selectedGradingData?.id || "",
        },
      },
    ],
  });
  const [updateStudentAssignmentGrade] = useUpdateStudentAssignmentGradeMutation({
    refetchQueries: [
      {
        query: GetUserSubmissionsByFacultyDocument,
        variables: {
          input: {
            facultyId: meData?.me?.id || "",
            searchQuery,
            page: submissionsPage,
            pageSize: submissionsPageSize,
            courseIds: selectedCourses,
            moduleIds: selectedModules,
            sortBy,
          },
        },
      },
      {
        query: GetGradingCenterDashboardDataDocument,
        variables: {
          facultyId: meData?.me?.id || "",
        },
      },
    ],
  });

  const { data: courseData, loading: courseLoading } = useGetCoursesByFacultyQuery({
    variables: {
      schoolId,
      userId: meData?.me?.id,
    },
    skip: !meData?.me?.id,
  });

  const { data: gradingCenterDashboardData, loading: gradingCenterDashboardLoading } =
    useGetGradingCenterDashboardDataQuery({
      variables: {
        facultyId: meData?.me?.id,
      },
      skip: !meData?.me?.id,
    });
  const gradingData = gradingCenterDashboardData?.getGradingCenterDashboardData?.data;

  const { data: assessmentSubmissionDetails, loading: assessmentSubmissionDetailsLoading } =
    useGetAssessmentSubmissionsDetailsQuery({
      variables: {
        assessmentSubmissionId: selectedGradingData?.id || "",
      },
      skip: !selectedGradingData?.id || selectedGradingData?.type !== "Assessment",
    });

  const { data: assessmentData, refetch: refetchAssessmentData } = useGetAssessmentSubmissionsByFacultyQuery({
    variables: {
      input: {
        facultyId: meData?.me?.id || "",
        searchQuery,
        page: assessmentsPage,
        pageSize: assessmentsPageSize,
        courseIds: selectedCourses,
        moduleIds: selectedModules,
        sortBy,
      },
    },
    skip: !meData?.me?.id,
  });

  const { data: userSubmissionsData, refetch: refetchUserSubmissionsData } = useGetUserSubmissionsByFacultyQuery({
    variables: {
      input: {
        facultyId: meData?.me?.id || "",
        searchQuery,
        page: submissionsPage,
        pageSize: submissionsPageSize,
        courseIds: selectedCourses,
        moduleIds: selectedModules,
        sortBy,
      },
    },
    skip: !meData?.me?.id,
  });

  const { refetch: refetchAttemptHistory } = useGetAssessmentSubmissionAttemptHistoryQuery({
    variables: {
      assessmentId: selectedGradingData?.assessmentId || "",
      userId: selectedGradingData?.userId || "",
    },
    skip: !selectedGradingData?.assessmentId || !selectedGradingData?.userId,
  });

  const filters = useMemo(() => {
    return {
      courses: courseData?.getCoursesByFaculty?.data?.map((course) => ({
        key: course.id,
        value: course.name,
      })),
      modules: courseData?.getCoursesByFaculty?.data
        ?.map((course) =>
          course.modules.map((module) => ({
            key: module.id,
            value: module.title,
          }))
        )
        .flat(),
    };
  }, [courseData]);

  useEffect(() => {
    setSelectedCourses(filters.courses?.map((course) => course.key));
    setSelectedModules(filters.modules?.map((module) => module.key));
  }, [courseData]);

  useEffect(() => {
    refetchAssessmentData();
    refetchUserSubmissionsData();
  }, [refetchAssessmentData, refetchUserSubmissionsData, meData]);

  const assessmentSubmissions = useMemo(
    () =>
      assessmentData?.getAssessmentSubmissionsByFaculty?.data?.assessmentSubmissions.map((assessment) => ({
        userName: assessment.user.name,
        userId: assessment.user.id,
        courseName: assessment.assessment.moduleHasAssessment.courseHasModules[0].name,
        moduleName: assessment.assessment.moduleHasAssessment.title,
        assessmentId: assessment.assessment.id,
        maximumPoints: assessment.maximumPoints,
        timeLimit: assessment.timeLimit,
        ...assessment,
        type: "Assessment",
      })),
    [assessmentData]
  );

  const submissions = useMemo(
    () =>
      userSubmissionsData?.getUserSubmissionsByFaculty?.data?.submissions.map((submission) => ({
        ...submission,
        type: "Assignment",
        totalPoints: submission.grade,
      })),
    [userSubmissionsData]
  );

  const handleViewResponse = (item: GradingData) => {
    setSelectedGradingData(item);
    // Make sure we're properly checking the type
    if (item.type === "Assessment") {
      setModalState(ModalState.VIEW_ASSESSMENT_RESPONSE);
    } else {
      setModalState(ModalState.VIEW_ASSIGNMENT_RESPONSE);
    }
  };

  const handleSaveAssignmentGrade = async (gradingData: GradingData, newScore: number, feedback?: string) => {
    setUpdateError(null);
    setActionResult(null);
    setIsSubmitting(true);

    await updateStudentAssignmentGrade({
      variables: {
        submissionId: gradingData.id,
        updatePayload: {
          grade: newScore,
          feedback,
          retakeAllowed: false,
          retakeDeadline: null,
        },
      },
    });

    setModalState(ModalState.CLOSED);
    setIsSubmitting(false);
  };

  const handleSaveAssessmentGrade = async (
    gradingData: GradingData,
    updatedGradeEntry: UpdateAssessmentSubmissionUpdatedGrade,
    closeModal: boolean
  ) => {
    setUpdateError(null);
    setIsSubmitting(true);

    try {
      await updateAssessmentSubmissionGrade({
        variables: {
          assessmentSubmissionId: gradingData.id,
          updatedGradeEntry,
          reviewWithoutAdjustment: !updatedGradeEntry.updatedEntityId ? true : false,
        },
      });

      if (gradingData.assessmentId && gradingData.userId) {
        refetchAttemptHistory({
          assessmentId: gradingData.assessmentId,
          userId: gradingData.userId,
        });
      }
    } catch (error) {
      if (error?.message?.includes("Evaluation is currently pending")) {
        setUpdateError(error.message);
      } else {
        setUpdateError(error.message);
      }
    }
    setIsSubmitting(false);

    if (closeModal) {
      setModalState(ModalState.CLOSED);
      setSelectedGradingData(null);
    }
  };

  const handleCloseModal = () => {
    setModalState(ModalState.CLOSED);
    setSelectedGradingData(null);
  };

  useEffect(() => {
    setAssessmentsPage(1);
    setSubmissionsPage(1);
  }, [searchQuery, sortBy]);

  useEffect(() => {
    refetchAssessmentData();
    refetchUserSubmissionsData();
  }, [searchQuery, assessmentsPage, submissionsPage, assessmentsPageSize, submissionsPageSize, sortBy]);

  return (
    <DefaultLayout pageHasChat={false}>
      <MotiView
        from={{ opacity: 0, translateY: 20 }}
        animate={{ opacity: 1, translateY: 0 }}
        delay={10}
        // @ts-expect-error - MotiView transition props have complex typing that's incompatible with simple objects
        transition={{ type: "timing", duration: 500 }}>
        <CustomText size="m" textType="display" text="Grading Center" weight="ultraBold" />
      </MotiView>
      <CustomSpacing type="vertical" size="xl" />
      <View style={styles.contentContainer}>
        <DashboardDataContainer
          newSubmissionsToday={gradingData?.newSubmissionsToday}
          assignmentsAndAssessmentsToGrade={gradingData?.assignmentsAndAssessmentsToGrade}
          isLoading={gradingCenterDashboardLoading || gradingData === undefined}
        />
        <View style={styles.filterContainer}>
          <View style={styles.controlsContainer}>
            <SortingDropdown
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortDropdownOpen={sortDropdownOpen}
              setSortDropdownOpen={setSortDropdownOpen}
              sortItems={SORT_ITEMS}
              label="Sort by"
            />
            <CustomSearchInput searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          </View>
          <View style={styles.dropdownFilterContainer}>
            <CustomMultiSelect
              data={filters.courses}
              selectedItems={selectedCourses}
              setSelectedItems={setSelectedCourses}
              label="Course"
            />
            <CustomMultiSelect
              data={filters.modules}
              selectedItems={selectedModules}
              setSelectedItems={setSelectedModules}
              label="Module"
            />
          </View>
        </View>
        <View style={styles.tableContainer}>
          <CustomText text="Assessment Submissions" style={styles.tableTitle} weight="bold" />
          <GradingTableContainer
            gradingData={assessmentSubmissions ?? []}
            page={assessmentsPage}
            pageSize={assessmentsPageSize}
            setPage={setAssessmentsPage}
            setPageSize={setAssessmentsPageSize}
            totalCount={assessmentData?.getAssessmentSubmissionsByFaculty?.data?.totalCount}
            handleRowPress={handleViewResponse}
          />
        </View>
        <CustomSpacing type="vertical" size="xl" />
        <View style={styles.tableContainer}>
          <CustomText text="Assignment Submissions" style={styles.tableTitle} weight="bold" />
          <GradingTableContainer
            gradingData={submissions ?? []}
            page={submissionsPage}
            pageSize={submissionsPageSize}
            setPage={setSubmissionsPage}
            setPageSize={setSubmissionsPageSize}
            totalCount={userSubmissionsData?.getUserSubmissionsByFaculty?.data?.totalCount}
            handleRowPress={handleViewResponse}
          />
        </View>
      </View>
      {modalState === ModalState.VIEW_ASSESSMENT_RESPONSE && (
        <AssessmentGradingModalContainer
          isVisible={true}
          onClose={handleCloseModal}
          isLoading={assessmentSubmissionDetailsLoading}
          gradingData={{
            ...(assessmentSubmissionDetails?.getAssessmentSubmissionDetails?.data as unknown as GradingData),
            type: "Assessment",
          }}
          onSave={handleSaveAssessmentGrade}
          isSubmitting={isSubmitting}
          updateError={updateError}
          canEditScore={true}
        />
      )}

      {modalState === ModalState.VIEW_ASSIGNMENT_RESPONSE && (
        <AssignmentSubmissionGradingModal
          isVisible={true}
          onClose={handleCloseModal}
          gradingData={selectedGradingData}
          onSave={handleSaveAssignmentGrade}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Action Result Feedback */}
      {actionResult && (
        <View style={styles.feedbackContainer}>
          <CustomText text={actionResult} size="s" style={styles.feedbackText} />
        </View>
      )}

      {/* Error Feedback */}
      {updateError && (
        <View style={[styles.feedbackContainer, styles.errorContainer]}>
          <CustomText text={updateError} size="s" style={styles.errorText} />
        </View>
      )}
    </DefaultLayout>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    position: "relative",
    zIndex: 1,
  },
  filterContainer: {
    backgroundColor: "#f9fafb",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 16,
    zIndex: 50,
  },
  dropdownFilterContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    position: "relative",
    zIndex: 50,
  },
  tableContainer: {
    marginTop: 12,
    paddingHorizontal: 12,
    // @ts-expect-error - zIndex is not a valid property for View
    zIndex: "auto",
  },
  tableTitle: {
    fontSize: 16,
    color: "#344054",
  },
  controlsContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    zIndex: 3000,
    elevation: 3000,
  },
  feedbackContainer: {
    position: "absolute",
    bottom: 20,
    right: 20,
    backgroundColor: "#10B981",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
    maxWidth: 300,
  },
  feedbackText: {
    color: "#FFFFFF",
    fontWeight: "500",
  },
  errorContainer: {
    backgroundColor: "#EF4444",
  },
  errorText: {
    color: "#FFFFFF",
    fontWeight: "500",
  },
});

export default GradingCenterView;
